/**
 * A seller's vacation mode (Reverb's and Etsy's): while it is on, buyers
 * can't buy, add to cart or make offers on the seller's listings. The server
 * enforces it (server/api-util/vacation.js, read the same way); the pages
 * only say so.
 *
 * Saved on the seller's profile, publicData.vacation:
 *   { on: bool, until: 'YYYY-MM-DD' | null, message: string ≤ 200 }
 */

export const VACATION_MESSAGE_MAX = 200;
export const SELLER_AWAY_ERROR_CODE = 'seller-away';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/** When an `until` stops counting: the end of that day (UTC) for a date, the time itself otherwise. */
const endOf = until => {
  if (typeof until !== 'string' || !until) return null;
  const isDate = DATE_ONLY.test(until);
  const at = Date.parse(isDate ? `${until}T00:00:00.000Z` : until);
  if (!Number.isFinite(at)) return null;
  return isDate ? at + DAY_MS : at;
};

/**
 * The seller's vacation as it stands now, or null when they are not away:
 * off, never set, malformed, or past its `until`.
 *
 * @param {Object} user a user entity with its profile's publicData
 * @param {Date} [now]
 * @returns {{ on: true, until: string|null, message: string }|null}
 */
export const getSellerVacation = (user, now = new Date()) => {
  const raw = user?.attributes?.profile?.publicData?.vacation;
  if (!raw || typeof raw !== 'object' || raw.on !== true) return null;
  const end = endOf(raw.until);
  if (end != null && now.getTime() >= end) return null;
  const message =
    typeof raw.message === 'string' ? raw.message.trim().slice(0, VACATION_MESSAGE_MAX) : '';
  return { on: true, until: end != null ? raw.until : null, message };
};

/**
 * The `until` as a Date to format with intl.formatDate (timeZone 'UTC', so
 * a date reads as that date wherever the buyer is), or null.
 */
export const vacationUntilDate = vacation => {
  const until = vacation?.until;
  const end = endOf(until);
  if (end == null) return null;
  return new Date(DATE_ONLY.test(until) ? end - DAY_MS : end);
};

/** Formats the `until` for the pages ("Oct 15, 2026"), or null when there is none. */
export const formatVacationUntil = (intl, vacation) => {
  const date = vacationUntilDate(vacation);
  return date
    ? intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    : null;
};

/** Was this refusal from the server because the seller is away? (any of our endpoints) */
export const isSellerAwayError = error => {
  const errors = error?.apiErrors || error?.data?.errors || [];
  return (
    error?.code === SELLER_AWAY_ERROR_CODE ||
    errors.some(apiError => apiError?.code === SELLER_AWAY_ERROR_CODE)
  );
};
